import React, { useState } from 'react';
import { faWandMagicSparkles, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Button from 'components/UI/V3/Button/Button';
import styles from './NewAutomationDrawer.module.scss';

export type CreationMode = 'ai' | 'manually';

interface NewAutomationDrawerProps {
    onClose: () => void;
    onConfirm: (name: string, mode: CreationMode) => void;
}

const defaultName = () => {
    const now = new Date();
    return `Automation - ${now.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    })}, ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

export const NewAutomationDrawer: React.FC<NewAutomationDrawerProps> = ({ onClose, onConfirm }) => {
    const [name, setName] = useState(defaultName());
    const [mode, setMode] = useState<CreationMode>('manually');

    const handleCreate = () => {
        onConfirm(name.trim() || defaultName(), mode);
    };

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader title="New Automation" />
            <DrawerBody className={styles.body}>
                <div className={styles.field}>
                    <label className={styles.label}>
                        Name <span className={styles.required}>*</span>
                    </label>
                    <Input
                        id="automation-name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>

                <p className={styles.sectionLabel}>Select how you&apos;ll create the automation</p>

                <div className={styles.options}>
                    <div
                        className={`${styles.option} ${mode === 'ai' ? styles.selected : ''}`}
                        onClick={() => setMode('ai')}
                        role="radio"
                        aria-checked={mode === 'ai'}
                        tabIndex={0}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? setMode('ai') : undefined)}
                    >
                        <FontAwesomeIcon
                            icon={faWandMagicSparkles}
                            className={styles.optionIcon}
                        />
                        <div className={styles.optionText}>
                            <span className={styles.optionTitle}>Create with AI</span>
                            <span className={styles.optionDescription}>
                                Save time and create a automation with your own words.
                            </span>
                        </div>
                        <span className={`${styles.radio} ${mode === 'ai' ? styles.radioSelected : ''}`} />
                    </div>

                    <div
                        className={`${styles.option} ${mode === 'manually' ? styles.selected : ''}`}
                        onClick={() => setMode('manually')}
                        role="radio"
                        aria-checked={mode === 'manually'}
                        tabIndex={0}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? setMode('manually') : undefined)}
                    >
                        <FontAwesomeIcon
                            icon={faPenToSquare}
                            className={styles.optionIcon}
                        />
                        <div className={styles.optionText}>
                            <span className={styles.optionTitle}>Create Manually</span>
                            <span className={styles.optionDescription}>
                                Go to builder and manually create a trigger and actions.
                            </span>
                        </div>
                        <span className={`${styles.radio} ${mode === 'manually' ? styles.radioSelected : ''}`} />
                    </div>
                </div>
            </DrawerBody>

            <DrawerFooter>
                <Button
                    label="Create"
                    onClick={handleCreate}
                    variant="black"
                    size="large"
                    fullWidth
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default NewAutomationDrawer;
